import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';
import { utilityServices, quickLinks, conservationPrograms, utilityStats } from '../../mocks/utilities';

export default function UtilitiesPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeService, setActiveService] = useState<number | null>(null);

  useSEO({
    title: 'Roseville Utilities Services California | Electric Water Wastewater',
    description: 'Roseville California utility services including electric, water, wastewater, solid waste, and recycling. Pay bills online, report outages, conservation programs, and 24/7 emergency services. Reliable utilities serving Roseville, CA residents and businesses.',
    keywords: 'Roseville utilities, Roseville electric, Roseville water, Roseville California utilities, utility bill payment, conservation programs',
    canonical: '/utilities',
    ogImage: 'https://public.readdy.ai/ai/img_res/8516c56bda2262814e837fb307d6cd36.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          'Utility Services - Roseville, California',
          'Comprehensive utility services including electric, water, wastewater, and solid waste management',
          '/utilities'
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Utilities', url: '/utilities' }
        ]),
        {
          '@type': 'GovernmentService',
          name: 'Roseville Utility Services',
          description: 'City of Roseville utility services providing electric, water, wastewater, and solid waste services',
          provider: {
            '@type': 'GovernmentOrganization',
            name: 'City of Roseville',
            telephone: '+1-916-774-5300',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '5098 Foothills Blvd, Suite 3-402',
              addressLocality: 'Roseville',
              addressRegion: 'CA',
              postalCode: '95747',
              addressCountry: 'US'
            }
          },
          areaServed: {
            '@type': 'City',
            name: 'Roseville',
            '@id': 'https://en.wikipedia.org/wiki/Roseville,_California'
          },
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/utilities`,
          hoursAvailable: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '08:00',
            closes: '17:00'
          }
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How do I pay my Roseville utility bill?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'You can pay your Roseville utility bill online 24/7 through the city\'s secure portal, via the mobile app, by setting up auto-pay, by phone, by mail, or in person at City Hall during business hours (Mon–Fri, 8 AM–5 PM).'
              }
            },
            {
              '@type': 'Question',
              name: 'How do I report a power outage in Roseville?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Call Roseville Electric Utility at (916) 774-5748 to report power outages. The emergency line is available 24/7. You can also report outages through the city\'s online portal or mobile app.'
              }
            },
            {
              '@type': 'Question',
              name: 'What utility services does the City of Roseville provide?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The City of Roseville provides electric, water, wastewater, solid waste, recycling, and stormwater management services to residents and businesses throughout Roseville, California.'
              }
            }
          ]
        }
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const colorClasses: Record<string, { bg: string; text: string; light: string; border: string }> = {
    amber: { bg: 'bg-amber-500', text: 'text-amber-600', light: 'bg-amber-50', border: 'border-amber-200' },
    sky: { bg: 'bg-sky-500', text: 'text-sky-600', light: 'bg-sky-50', border: 'border-sky-200' },
    emerald: { bg: 'bg-emerald-500', text: 'text-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-200' },
    green: { bg: 'bg-green-500', text: 'text-green-600', light: 'bg-green-50', border: 'border-green-200' },
    orange: { bg: 'bg-orange-500', text: 'text-orange-600', light: 'bg-orange-50', border: 'border-orange-200' },
    violet: { bg: 'bg-violet-500', text: 'text-violet-600', light: 'bg-violet-50', border: 'border-violet-200' }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer">
              <div className={`text-2xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                <span className="font-black">ROSEVILLE</span>
                <span className="text-teal-500">.org</span>
              </div>
            </Link>
            
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Home</Link>
              <Link to="/recreation" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Recreation</Link>
              <Link to="/real-estate" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}>Real Estate</Link>
              <Link to="/utilities" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-teal-600 font-semibold' : 'text-teal-300'
              }`}>Utilities</Link>
            </div>

            <a href="#pay-bill" className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
              Pay Bill
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Modern%20city%20utility%20infrastructure%20with%20power%20lines%2C%20water%20treatment%20facility%2C%20and%20solar%20panels%20against%20a%20beautiful%20sunset%20sky%20in%20California%2C%20clean%20energy%20and%20sustainable%20city%20services%20concept%2C%20professional%20photography%20with%20warm%20golden%20hour%20lighting&width=1920&height=600&seq=utilities001&orientation=landscape"
            alt="Roseville Utilities"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <i className="ri-flashlight-line text-amber-400"></i>
            <span className="text-white text-sm font-medium">City Services</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
            Roseville <span className="text-teal-400">Utilities</span>
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Reliable, affordable utility services powering homes and businesses throughout Roseville. Electric, water, wastewater, and more.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#services" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-full font-medium transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap">
              <i className="ri-list-check mr-2"></i>View Services
            </a>
            <a href="#pay-bill" className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-8 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-bank-card-line mr-2"></i>Pay Your Bill
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-8 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {utilityStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-2xl text-teal-600`}></i>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="bg-gradient-to-r from-teal-600 to-teal-700 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            {quickLinks.map((link, index) => (
              <a 
                key={index}
                href={link.url}
                className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-5 py-2.5 rounded-full text-white transition-all cursor-pointer"
              >
                <i className={`${link.icon} text-lg`}></i>
                <span className="text-sm font-medium whitespace-nowrap">{link.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Utility Services */}
      <section id="services" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-teal-100 text-teal-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              Our Services
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Utility Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Roseville provides comprehensive utility services to ensure reliable power, clean water, and sustainable waste management for our community.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {utilityServices.map((service) => {
              const colors = colorClasses[service.color];
              return (
                <div 
                  key={service.id}
                  className={`bg-white rounded-2xl border ${activeService === service.id ? colors.border : 'border-gray-100'} p-6 hover:shadow-xl transition-all duration-300 cursor-pointer`}
                  onMouseEnter={() => setActiveService(service.id)}
                  onMouseLeave={() => setActiveService(null)}
                >
                  <div className={`w-14 h-14 ${colors.light} rounded-xl flex items-center justify-center mb-4`}>
                    <i className={`${service.icon} text-2xl ${colors.text}`}></i>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{service.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm">
                      <i className="ri-phone-line text-gray-400 mr-2"></i>
                      <span className="text-gray-700">{service.phone}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <i className="ri-time-line text-gray-400 mr-2"></i>
                      <span className="text-gray-700">{service.hours}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <i className="ri-price-tag-3-line text-gray-400 mr-2"></i>
                      <span className="text-gray-700">{service.rates}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {service.features.map((feature, idx) => (
                      <span key={idx} className={`${colors.light} ${colors.text} px-3 py-1 rounded-full text-xs font-medium`}>
                        {feature}
                      </span>
                    ))}
                  </div>

                  <button className={`w-full ${colors.bg} hover:opacity-90 text-white py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap`}>
                    Learn More
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Conservation Programs */}
      <section className="py-20 px-6 bg-gradient-to-br from-green-50 to-teal-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              Save Money & Energy
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Conservation Programs</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Take advantage of rebates and incentives to reduce your utility bills while helping protect the environment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {conservationPrograms.map((program) => (
              <div key={program.id} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${program.icon} text-2xl text-green-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{program.title}</h3>
                <p className="text-gray-600 text-sm mb-3">{program.description}</p>
                <div className="bg-green-50 rounded-lg px-3 py-2">
                  <p className="text-green-700 text-sm font-medium">{program.savings}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a href="#rebates" className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-gift-line mr-2"></i>View All Rebates
            </a>
          </div>
        </div>
      </section>

      {/* Pay Bill Section */}
      <section id="pay-bill" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-3xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="p-10 lg:p-12">
                <h2 className="text-3xl font-bold text-white mb-4">Pay Your Utility Bill</h2>
                <p className="text-white/80 mb-8">
                  Multiple convenient payment options available. Pay online, by phone, by mail, or in person at City Hall.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start space-x-4 bg-white/10 rounded-xl p-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-computer-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">Online Payment</h4>
                      <p className="text-white/70 text-sm">Pay 24/7 through our secure online portal</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4 bg-white/10 rounded-xl p-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-smartphone-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">Mobile App</h4>
                      <p className="text-white/70 text-sm">Download our app for easy bill management</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4 bg-white/10 rounded-xl p-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-repeat-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">Auto-Pay</h4>
                      <p className="text-white/70 text-sm">Set up automatic payments and never miss a due date</p>
                    </div>
                  </div>
                </div>

                <button className="bg-white text-teal-700 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-bank-card-line mr-2"></i>Pay Now
                </button>
              </div>
              
              <div className="hidden lg:block relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Person%20using%20smartphone%20to%20pay%20utility%20bills%20online%2C%20modern%20mobile%20banking%20app%20interface%2C%20clean%20professional%20lifestyle%20photography%20with%20soft%20lighting%2C%20hands%20holding%20phone%20with%20payment%20screen&width=600&height=500&seq=utilities002&orientation=portrait"
                  alt="Pay Bills Online"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-16 px-6 bg-rose-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-rose-100">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center">
                  <i className="ri-alarm-warning-line text-3xl text-rose-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">24/7 Emergency Services</h3>
                  <p className="text-gray-600">Report power outages, water main breaks, or gas leaks immediately</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:9167745300" className="flex items-center justify-center space-x-2 bg-rose-600 hover:bg-rose-700 text-white px-6 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line"></i>
                  <span>(916) 774-5300</span>
                </a>
                <a href="#report-outage" className="flex items-center justify-center space-x-2 bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-error-warning-line"></i>
                  <span>Report Outage</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Contact Utility Services</h2>
            <p className="text-gray-600">Our team is here to help with all your utility needs</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Visit Us</h3>
              <p className="text-gray-600 text-sm">
                5098 Foothills Blvd, Suite 3-402<br />
                Roseville, CA 95747
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-600 text-sm">
                Main: (916) 238-8515<br />
                Utilities: (916) 774-5300
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Office Hours</h3>
              <p className="text-gray-600 text-sm">
                Monday - Friday<br />
                8:00 AM - 5:00 PM
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-800 via-teal-900 to-teal-800 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="text-2xl font-bold mb-4">
                <span className="font-black">ROSEVILLE</span>
                <span className="text-amber-400">.org</span>
              </div>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                City of Roseville - Utility Services
              </p>
              <p className="text-white/80 text-sm">
                5098 Foothills Blvd, Suite 3-402<br />
                Roseville, California 95747
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Utility Services</h4>
              <ul className="space-y-2">
                <li><a href="#electric" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Electric</a></li>
                <li><a href="#water" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Water</a></li>
                <li><a href="#wastewater" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Wastewater</a></li>
                <li><a href="#solidwaste" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Solid Waste</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/recreation" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Recreation</Link></li>
                <li><Link to="/real-estate" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Real Estate</Link></li>
                <li><a href="#pay-bill" className="text-white/80 hover:text-white text-sm transition-colors cursor-pointer">Pay Bill</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Connect With Us</h4>
              <div className="flex space-x-3 mb-6">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-white/60 text-sm">
                © 2025 City of Roseville, California. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <a href="#privacy" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</a>
                <a href="#terms" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Terms of Use</a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
